import { X, Plus, Minus } from 'lucide-react';
import { CartItem as CartItemType } from '@/types';
import { formatPrice } from '@/lib/utils';
import { calculateCartItemDiscount, getEffectiveVariationPrice } from '@/lib/discount';
import { Link } from 'react-router-dom';

interface CartItemProps {
  item: CartItemType;
  onQuantityChange: (id: number, quantity: number) => void;
  onVariationQuantityChange: (itemId: number, variation: string, change: number) => void;
  onRemoveVariation: (itemId: number, variation: string) => void;
  onRemoveFromCart: (id: number) => void;
  soldOutItems: number[];
  stockLevels: Record<number, { total: number; variations: Record<string, number> }>;
}

export default function CartItem({
  item,
  onQuantityChange,
  onVariationQuantityChange,
  onRemoveVariation,
  onRemoveFromCart,
  soldOutItems,
  stockLevels,
}: CartItemProps) {
  const isSoldOut = soldOutItems.includes(item.id);
  const stockLevel = stockLevels[item.id];
  const { totalOriginalPrice, totalDiscountValue, totalFinalPrice, discountInfo } = calculateCartItemDiscount(item);

  const getVariationAmount = (variationName: string, quantity: number) => {
    const selectedVar = item.selectedVariations?.find(v => v.variation === variationName);
    if (selectedVar) {
      if (selectedVar.hasQuantityPricing && selectedVar.quantityPricing?.length) {
        const tier = selectedVar.quantityPricing.find((tier) => {
          const min = parseInt(tier.minQuantity || '0') || 0;
          const max = parseInt(tier.maxQuantity || '0') || 0;
          return quantity >= min && quantity <= max;
        });
        if (tier && parseFloat(tier.price) > 0) {
          return parseFloat(tier.price);
        }
      }
      return parseFloat(selectedVar.variation_amount || '0');
    }

    const variationData = item.variations.find(v => v.variation === variationName);
    return variationData ? parseFloat(variationData.variation_amount) : 0;
  };

  const calculateVariationTotal = (variation: string, quantity: number) => {
    const amount = getVariationAmount(variation, quantity);
    return amount * quantity;
  };

  return (
    <li className={`p-6 ${isSoldOut ? 'opacity-50' : ''}`}>
      <div className="flex items-start gap-4">
        <Link to={`/product/${item.id}`} className="relative flex-shrink-0">
          {item.images && item.images.length > 0 && item.images[0] ? (
            <img
              src={item.images[0]}
              alt={item.produt_name}
              className="w-24 h-24 object-cover rounded-lg"
            />
          ) : (
            <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="mt-1 text-xs text-gray-500">No Image</p>
              </div>
            </div>
          )}
          {isSoldOut && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
              <span className="text-white font-bold text-sm">SOLD OUT</span>
            </div>
          )}
        </Link>

        <div className="flex-1 min-w-0">
          <div className="flex justify-between">
            <Link to={`/product/${item.id}`}>
              <h3 className="text-base font-medium text-gray-900 truncate hover:text-brand-primary transition-colors">
                {item.produt_name}
              </h3>
            </Link>
            <button
              onClick={() => onRemoveFromCart(item.id)}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {item.selectedVariations && item.selectedVariations.length > 0 ? (
            <div className="mt-2 space-y-3">
              {item.selectedVariations.filter(v => v.quantity > 0).map((variation) => {
                const selectedUnitPrice = getVariationAmount(variation.variation, variation.quantity);
                const effectiveUnitPrice = getEffectiveVariationPrice(item, variation, item.selectedVariations || []);
                const variationTotal = effectiveUnitPrice * variation.quantity;

                return (
                  <div
                    key={variation.variation}
                    className="grid grid-cols-1 gap-2 rounded-2xl border border-gray-200 bg-white p-3 sm:grid-cols-[1fr_auto] sm:items-center"
                  >
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-gray-700">{variation.variation}</span>
                        <span className="text-xs text-gray-500">(qty {variation.quantity})</span>
                      </div>
                      <p className="text-sm text-gray-500">
                        unit: {formatPrice(selectedUnitPrice)}
                        {effectiveUnitPrice !== selectedUnitPrice && (
                          <span className="ml-2 text-emerald-600 font-semibold">
                            {formatPrice(effectiveUnitPrice)} applied
                          </span>
                        )}
                      </p>
                      <p className="text-sm font-bold text-gray-900">line total: {formatPrice(variationTotal)}</p>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-1">
                      <button
                        onClick={() => onVariationQuantityChange(item.id, variation.variation, -1)}
                        disabled={isSoldOut}
                        className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-8 text-center text-gray-900 font-semibold">{variation.quantity}</span>
                      <button
                        onClick={() => onVariationQuantityChange(item.id, variation.variation, 1)}
                        disabled={isSoldOut || (stockLevel?.variations[variation.variation] || 0) <= variation.quantity}
                        className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onRemoveVariation(item.id, variation.variation)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {discountInfo.hasDiscount && (
                <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-yellow-800">
                      Bulk Discount ({discountInfo.threshold}+ items):
                    </span>
                    <span className="font-medium text-yellow-800">
                      {discountInfo.discountType === 'percentage' 
                        ? `${discountInfo.discountAmount}% OFF`
                        : `${formatPrice(discountInfo.discountAmount)} OFF each`
                      }
                    </span>
                  </div>
                  {discountInfo.isEligible ? (
                    <div className="mt-2 flex justify-between items-center text-sm">
                      <span className="text-green-700 font-medium">
                        ✓ Discount Applied
                      </span>
                      <span className="text-green-700 font-bold">
                        Save {formatPrice(totalDiscountValue)}
                      </span>
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-yellow-700">
                      Add {discountInfo.threshold - discountInfo.currentQuantity} more to qualify
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-2">
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-sm text-gray-500">
                    Price: {discountInfo.isEligible ? (
                      <>
                        <span className="line-through">{formatPrice(parseInt(item.sellingprice))}</span>
                        <span className="ml-2 text-brand-primary font-bold">
                          {formatPrice(discountInfo.finalPrice)}
                        </span>
                      </>
                    ) : (
                      formatPrice(parseInt(item.sellingprice))
                    )}
                  </span>
                  {discountInfo.hasDiscount && (
                    <span className="text-xs text-yellow-600">
                      {discountInfo.isEligible 
                        ? `Discount applied!`
                        : `Buy ${discountInfo.threshold}+ for ${discountInfo.discountType === 'percentage' ? `${discountInfo.discountAmount}%` : formatPrice(discountInfo.discountAmount)} off`
                      }
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => onQuantityChange(item.id, item.quantity - 1)}
                    disabled={isSoldOut || item.quantity <= 1}
                    className="p-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-8 text-center text-gray-900">
                    {item.quantity}
                  </span>
                  <button
                    onClick={() => onQuantityChange(item.id, item.quantity + 1)}
                    disabled={isSoldOut || (stockLevel?.total || 0) <= item.quantity}
                    className="p-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <span className="text-sm font-medium text-gray-900 ml-4">
                    = {formatPrice(totalFinalPrice)}
                  </span>
                </div>
              </div>
              
              {discountInfo.hasDiscount && (
                <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-yellow-800">
                      Bulk Discount ({discountInfo.threshold}+ items):
                    </span>
                    <span className="font-medium text-yellow-800">
                      {discountInfo.discountType === 'percentage' 
                        ? `${discountInfo.discountAmount}% OFF`
                        : `${formatPrice(discountInfo.discountAmount)} OFF each`
                      }
                    </span>
                  </div>
                  {discountInfo.isEligible ? (
                    <div className="mt-2 flex justify-between items-center text-sm">
                      <span className="text-green-700 font-medium">
                        ✓ Discount Applied
                      </span>
                      <span className="text-green-700 font-bold">
                        Save {formatPrice(totalDiscountValue)}
                      </span>
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-yellow-700">
                      Add {discountInfo.threshold - discountInfo.currentQuantity} more to qualify
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

